import React, { useMemo, useCallback } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { AiAgentChat } from '@/features/ai-agents/chat/ai-agent-chat';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { useFeatureBit, useLoggedInAccount } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ContextualAttachment } from '@/features/ai-agents/shared/models';

export function AiComponentPrompt() {
  const aiSessionId = useAiComponentContext((ctx) => ctx.sessionId);
  const onPromptNewSession = useAiComponentContext((ctx) => ctx.onNewSessionId);
  const onPromptReply = useAiComponentContext((ctx) => ctx.onPromptReply);
  const onLoadingChanged = useAiComponentContext((ctx) => ctx.onLoadingChange);
  const onNewMessageSent = useAiComponentContext((ctx) => ctx.onNewMessageSent);
  const onSessionLoaded = useAiComponentContext((ctx) => ctx.onSessionLoaded);
  const sessionData = useAiComponentContext((ctx) => ctx.sessionData);
  const selectedAgent = useAiComponentContext((ctx) => ctx.selectedAgent);
  const starters = useAiComponentContext((ctx) => ctx.starters);
  const contextualAttachments = useAiComponentContext(
    (ctx) => ctx.contextualAttachments,
  );
  const onEditAttachmentFromContext = useAiComponentContext(
    (ctx) => ctx?.onEditAttachment,
  );
  const placeholder = useAiComponentContext((ctx) => ctx.placeholder);
  const initialMessage = useAiComponentContext((ctx) => ctx.initialMessage);
  const alias = useMemo(() => useLoggedInAccount()?.store?.alias || ``, []);
  useSignals();

  const enableAgentSwitching = useFeatureBit(FeatureBit.SHOW_AI_MODEL_SWITCHER);

  const key = `${selectedAgent.value.id}-${
    aiSessionId.value?.includes(`restored`) ? aiSessionId.value : ``
  }`;

  const handleAgentSelect = (agent: {
    type: 'agent' | 'team' | 'workflow';
    id: string;
  }) => {
    selectedAgent.value = agent;
  };

  const onEditAttachment = useCallback(
    (oldAttachment: ContextualAttachment, newSelector: string) => {
      if (onEditAttachmentFromContext) {
        onEditAttachmentFromContext(
          contextualAttachments,
          oldAttachment,
          newSelector,
        );
      }
    },
    [onEditAttachmentFromContext, contextualAttachments],
  );

  return (
    <AiAgentChat
      key={key}
      userIdOverride={alias}
      agentId={selectedAgent.value.id}
      agentType={selectedAgent.value.type}
      enableVoiceInput={false}
      enableExamples={false}
      enableAgentSelection={enableAgentSwitching}
      sessionData={sessionData.value || {}}
      padding="0rem"
      initialSessionId={aiSessionId.value || ``}
      onLoadingChanged={onLoadingChanged}
      onNewMessageSent={onNewMessageSent}
      onNewSession={onPromptNewSession}
      onSessionLoaded={onSessionLoaded}
      onNewReply={onPromptReply}
      onAgentSelect={handleAgentSelect}
      enableFilesUpload
      inputBottom="0.3rem"
      inputLeft="0"
      inputRight="0"
      starters={starters}
      contextualAttachments={contextualAttachments}
      onEditAttachment={onEditAttachment}
      placeholder={placeholder}
      initialMessage={initialMessage.value}
    />
  );
}
